// ascii-sharks, MIT (c) 2026 drewdi. See LICENSE and SOURCE.json.
// Adapted from stackedlol/ascii-sharks (MIT, copyright 2026 drewdi).
// Source revision and original license are retained next to this module.
import { AsciiRenderer } from './renderer';
import { World } from './world';
const STEP = 1 / 60;
export class OceanEngine {
    constructor(canvas, onState) {
        this.canvas = canvas;
        this.raf = 0;
        this.last = 0;
        this.acc = 0;
        this.visible = true;
        this.disposed = false;
        this.paused = false;
        this.resize = () => {
            const rect = this.canvas.getBoundingClientRect();
            if (rect.width < 1 || rect.height < 1 || this.disposed)
                return;
            this.renderer.resize(rect.width, rect.height, Math.min(window.devicePixelRatio || 1, 1.5));
            this.world.resize(rect.width, rect.height, this.renderer.cellWidth, this.renderer.cellHeight);
            if (this.world.time < 1)
                for (let i = 0; i < 60; i++)
                    this.world.step(STEP);
            this.draw(STEP);
        };
        this.schedule = () => {
            cancelAnimationFrame(this.raf);
            if (this.disposed)
                return;
            if (this.reducedMotion.matches || this.paused) {
                this.world.pointer.active = false;
                this.draw(STEP);
                this.state(this.reducedMotion.matches ? 'reduced' : 'paused');
                return;
            }
            if (!this.visible || document.hidden) {
                this.world.pointer.active = false;
                this.state('offscreen');
                return;
            }
            this.last = performance.now();
            this.acc = 0;
            this.state('running');
            this.raf = requestAnimationFrame(this.frame);
        };
        this.frame = (now) => {
            if (this.disposed)
                return;
            const dt = Math.min(.08, (now - this.last) / 1000);
            this.last = now;
            this.acc += dt * .7;
            let steps = 0;
            while (this.acc >= STEP && steps < 4) {
                this.world.step(STEP);
                this.acc -= STEP;
                steps++;
            }
            if (steps === 4)
                this.acc = 0;
            this.draw(dt);
            this.raf = requestAnimationFrame(this.frame);
        };
        this.preferenceChanged = () => this.schedule();
        this.pointerLeave = () => { this.world.pointer.active = false; };
        this.pointerMove = (event) => {
            if (this.paused || this.reducedMotion.matches || event.pointerType === 'touch')
                return;
            const target = event.target;
            if (target === null || target === void 0 ? void 0 : target.closest('input, textarea, button, a, label, .trial-card')) {
                this.world.pointer.active = false;
                return;
            }
            const rect = this.canvas.getBoundingClientRect();
            const x = event.clientX - rect.left, y = event.clientY - rect.top;
            this.world.pointer = { x, y, active: x >= 0 && y >= 0 && x <= rect.width && y <= rect.height };
        };
        this.onState = onState;
        const mobile = canvas.getBoundingClientRect().width < 700;
        this.world = new World(mobile ? [{ kind: 'classic', size: .8 }] : [{ kind: 'hammerhead', size: 1 }, { kind: 'classic', size: .76 }], mobile ? 28 : 60);
        this.renderer = new AsciiRenderer(canvas);
        this.renderer.setFontFamily('ui-monospace, SFMono-Regular, Menlo, Consolas, monospace');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.resizeObserver = new ResizeObserver(this.resize);
        this.resizeObserver.observe(canvas);
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
            this.visible = entry.isIntersecting;
            this.schedule();
        });
        this.intersectionObserver.observe(canvas);
        this.reducedMotion.addEventListener('change', this.preferenceChanged);
        document.addEventListener('visibilitychange', this.schedule);
        window.addEventListener('pointermove', this.pointerMove, { passive: true });
        window.addEventListener('blur', this.pointerLeave);
        document.documentElement.addEventListener('pointerleave', this.pointerLeave);
        this.resize();
        this.schedule();
    }
    setPaused(paused) {
        this.paused = paused;
        this.schedule();
    }
    destroy() {
        this.disposed = true;
        cancelAnimationFrame(this.raf);
        this.resizeObserver.disconnect();
        this.intersectionObserver.disconnect();
        this.reducedMotion.removeEventListener('change', this.preferenceChanged);
        document.removeEventListener('visibilitychange', this.schedule);
        window.removeEventListener('pointermove', this.pointerMove);
        window.removeEventListener('blur', this.pointerLeave);
        document.documentElement.removeEventListener('pointerleave', this.pointerLeave);
        this.canvas.dataset.motion = 'destroyed';
    }
    draw(dt) {
        this.renderer.render(this.world, dt);
    }
    state(value) {
        this.canvas.dataset.motion = value;
        this.onState(value);
    }
}
